// src/components/OrderCartPage.jsx
import React, { useState, useEffect } from 'react';
import {
    FaTrashAlt, FaHeart, FaFileAlt, FaPercentage, FaTruck, FaMoneyBillAlt, FaTag, FaCheckCircle
} from 'react-icons/fa';

export default function OrderCartPage() {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
            setIsSmallMobile(window.innerWidth <= 480);
        };

        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const styles = {
        // --- Main Layout & Global Styles ---
        mainWrapper: {
            display: 'flex',
            flexDirection: 'column', // Changed to column to place heading separately
            alignItems: 'center', // Center content horizontally
            minHeight: '100vh',
            width: '100%',
            backgroundColor: 'transparent', // Main background color
            padding: isSmallMobile ? '10px' : (isMobile ? '20px' : '40px'),
            boxSizing: 'border-box',
            fontFamily: 'Segoe UI, Arial, sans-serif',
            overflowY: 'auto',
            overflowX: 'hidden',
        },
        pageTitle: {
            fontSize: '17px',
            fontWeight: 'bold',
            color: '#000', // Black for "Order Cart" heading
            marginBottom: '20px', // Space below the title
            width: '100%',
            maxWidth: '1200px', // Match content max width
            textAlign: 'left',
            paddingLeft: '20px', // Aligned with the card content below
            boxSizing: 'border-box', // Include padding in width
        },
        pageContent: {
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', // Two columns for desktop, single for mobile
            gap: '15px', // Reduced gap between columns
            width: '100%',
            maxWidth: '1200px', // Max width for the content columns
        },
        column: {
            display: 'flex',
            flexDirection: 'column',
            gap: '15px', // Reduced gap between cards within a column
        },
        card: {
            backgroundColor: '#000', // Black background for cards
            borderRadius: '10px', // Rounded corners
            padding: '15px 20px', // Slightly reduced padding for compactness
            boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
            color: '#E5D0A5', // Beige text
        },
        cardHeader: {
            fontSize: '15px', // Slightly smaller font for compactness
            fontWeight: 'bold',
            marginBottom: '10px', // Reduced margin
            color: '#E5D0A5',
        },

        // --- Cart Header ---
        cartHeaderBox: {
            backgroundColor: '#000',
            borderRadius: '10px',
            padding: '10px 20px', // Reduced padding
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            // marginBottom: '15px', // Gap handled by column gap
            color: '#E5D0A5',
            fontSize: '14px',
        },
        clearCartButton: {
            backgroundColor: 'transparent',
            border: 'none',
            color: '#E5D0A5',
            fontSize: '14px',
            cursor: 'pointer',
            textDecoration: 'underline',
        },

        // --- Product Item Card ---
        productItemCard: {
            backgroundColor: '#000',
            borderRadius: '10px',
            padding: '15px 20px', // Keep consistent with other cards
            // marginBottom: '15px', // Gap handled by column gap
            display: 'flex',
            alignItems: 'flex-start',
            gap: '15px',
            color: '#E5D0A5',
            flexWrap: 'wrap',
        },
        productImageContainer: {
            width: '70px', // Size of the image container
            height: '70px',
            borderRadius: '5px',
            overflow: 'hidden',
            flexShrink: 0,
            backgroundColor: '#D4AF37', // Gold background for bottle image
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        },
        productImage: {
            width: '100%',
            height: '100%',
            objectFit: 'contain',
        },
        productDetails: {
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            minWidth: '150px', // Ensure details don't get too cramped
        },
        productName: {
            fontSize: '15px', // Slightly smaller
            fontWeight: 'bold',
            color: '#E5D0A5', // Gold
            margin: '0 0 4px 0', // Reduced margin
        },
        productSize: {
            fontSize: '11px', // Slightly smaller
            color: '#BBB', // Lighter gold/gray
            margin: '0 0 8px 0', // Reduced margin
        },
        itemPrice: {
            fontSize: '11px', // Slightly smaller
            color: '#BBB',
            marginBottom: '8px', // Reduced margin
        },
        quantityControl: {
            display: 'flex',
            alignItems: 'center',
            border: '1px solid #E5D0A5', // Gold border
            borderRadius: '5px',
            width: '80px', // Slightly smaller width
            height: '28px', // Slightly smaller height
            overflow: 'hidden',
            marginBottom: '8px', // Reduced margin
        },
        qtyButton: {
            backgroundColor: '#000',
            color: '#E5D0A5',
            border: 'none',
            padding: '0 6px', // Reduced padding
            cursor: 'pointer',
            fontSize: '16px', // Slightly smaller font
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexGrow: 0,
            transition: 'background-color 0.2s ease',
        },
        qtyButtonHover: {
            backgroundColor: '#333',
        },
        qtyValue: {
            backgroundColor: '#000',
            color: '#E5D0A5',
            borderLeft: '1px solid #E5D0A5',
            borderRight: '1px solid #E5D0A5',
            padding: '0 4px', // Reduced padding
            fontSize: '13px', // Slightly smaller font
            width: '28px', // Adjusted width
            textAlign: 'center',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexGrow: 1,
        },
        productActions: {
            display: 'flex',
            gap: '10px', // Reduced gap
            marginTop: '8px', // Reduced margin
            flexBasis: '100%', // Take full width below details
            justifyContent: 'flex-start',
            flexWrap: 'wrap',
        },
        actionLink: {
            color: '#BBB',
            fontSize: '11px', // Slightly smaller
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '4px', // Reduced gap
            cursor: 'pointer',
            transition: 'color 0.2s ease',
        },
        actionLinkHover: {
            color: '#E5D0A5',
        },
        productTotal: {
            fontSize: '13px', // Slightly smaller
            fontWeight: 'bold',
            color: '#E5D0A5',
            marginLeft: 'auto', // Pushes to the right
            whiteSpace: 'nowrap',
            alignSelf: 'flex-end', // Align to bottom right of card
        },

        // --- Promo Code Section ---
        promoCodeCard: {
            backgroundColor: '#000',
            borderRadius: '10px',
            padding: '15px 20px', // Reduced padding for compactness
            color: '#E5D0A5',
            // marginBottom: '15px', // Gap handled by column gap
        },
        promoCodeHeader: {
            fontSize: '16px',
            fontWeight: 'bold',
            marginBottom: '10px', // Reduced margin
            color: '#E5D0A5',
        },
        promoInputGroup: {
            display: 'flex',
            gap: '8px', // Reduced gap
        },
        promoInput: {
            flexGrow: 1,
            padding: '8px', // Reduced padding
            borderRadius: '5px',
            border: '1px solid #D4AF37', // Gold border
            backgroundColor: '#000',
            color: '#E5D0A5',
            fontSize: '13px', // Slightly smaller font
            outline: 'none',
        },
        applyButton: {
            backgroundColor: '#D4AF37', // Gold background
            color: '#000', // Black text
            border: 'none',
            borderRadius: '5px',
            padding: '8px 12px', // Reduced padding
            fontSize: '13px', // Slightly smaller font
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease',
        },
        applyButtonHover: {
            backgroundColor: '#C0A020',
        },

        // --- Order Summary Section ---
        orderSummaryCard: {
            backgroundColor: '#000',
            borderRadius: '10px',
            padding: '15px 20px', // Reduced padding for compactness
            color: '#E5D0A5',
        },
        orderSummaryHeader: {
            fontSize: '16px',
            fontWeight: 'bold',
            marginBottom: '10px', // Reduced margin
            color: '#E5D0A5',
        },
        summaryRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '6px', // Reduced margin
            fontSize: '13px', // Slightly smaller font
            color: '#E5D0A5',
        },
        summaryLabel: {
            color: '#E5D0A5',
            fontWeight: 'normal',
            display: 'flex',
            alignItems: 'center',
            gap: '8px', // Reduced gap
        },
        summaryValue: {
            fontWeight: 'normal',
            color: '#E5D0A5',
        },
        summaryDivider: {
            borderBottom: '1px solid #333', // Thin dark divider
            margin: '8px 0', // Reduced margin
        },
        totalAmountRow: {
            fontSize: '16px', // Slightly smaller font
            fontWeight: 'bold',
            color: '#E5D0A5',
            textAlign: 'right',
            marginTop: '12px', // Reduced margin
        },
        totalAmountLabel: {
            fontWeight: 'normal',
            color: '#E5D0A5',
            fontSize: '16px',
        },
        totalAmountValue: {
            fontWeight: 'normal',
            color: '#E5D0A5',
            fontSize: '16px',
        },
        summaryIcon: {
            color: '#D4AF37', // Gold icon color
            fontSize: '14px', // Slightly smaller icon
        },

        // --- Estimated Delivery ---
        estimatedDeliveryBox: {
            backgroundColor: '#D4AF37', // Gold background
            borderRadius: '5px',
            padding: '8px 12px', // Reduced padding
            marginTop: '15px', // Reduced margin
            display: 'flex',
            alignItems: 'center',
            gap: '8px', // Reduced gap
            color: '#000', // Black text
            fontSize: '12px', // Slightly smaller font
            fontWeight: 'bold',
            textAlign: 'center',
            justifyContent: 'center',
        },
        deliveryIcon: {
            color: '#000', // Black icon
            fontSize: '16px', // Slightly smaller icon
        },

        // --- Action Buttons (Continue Shopping / Buy Now) ---
        bottomButtonGroup: {
            display: 'flex',
            gap: '10px', // Reduced gap
            marginTop: '15px', // Reduced margin
            flexWrap: 'wrap',
            justifyContent: 'flex-end', // Align to right
        },
        bottomButton: {
            padding: '8px 15px', // Reduced padding
            borderRadius: '5px',
            fontSize: '13px', // Slightly smaller font
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease',
            whiteSpace: 'nowrap',
            flexGrow: isSmallMobile ? 1 : 0,
        },
        continueShoppingButton: {
            backgroundColor: '#333', // Dark gray
            color: '#E5D0A5', // Beige text
            border: 'none',
        },
        continueShoppingButtonHover: {
            backgroundColor: '#555',
        },
        buyNowButton: {
            backgroundColor: '#D4AF37', // Gold
            color: '#000', // Black text
            border: 'none',
        },
        buyNowButtonHover: {
            backgroundColor: '#C0A020',
        },
    };

    const cartProducts = [
        { id: 1, name: 'Creed Aventus', size: '50ML', itemPrice: '250.00/5.00 TAX', quantity: 1, total: '2500.00', image: 'https://via.placeholder.com/70x70/D4AF37/000000?text=' }, // Using empty text for placeholder to get the correct background
        { id: 2, name: 'Creed Aventus', size: '50ML', itemPrice: '250.00/5.00 TAX', quantity: 1, total: '2500.00', image: 'https://via.placeholder.com/70x70/D4AF37/000000?text=' },
        { id: 3, name: 'Creed Aventus', size: '50ML', itemPrice: '250.00/5.00 TAX', quantity: 1, total: '2500.00', image: 'https://via.placeholder.com/70x70/D4AF37/000000?text=' },
        { id: 4, name: 'Creed Aventus', size: '50ML', itemPrice: '250.00/5.00 TAX', quantity: 1, total: '2500.00', image: 'https://via.placeholder.com/70x70/D4AF37/000000?text=' },
    ];

    return (
        <div style={styles.mainWrapper}>
            {/* Page Title - outside the grid, at the very top left */}
            <h1 style={styles.pageTitle}>Order Cart</h1>

            <div style={styles.pageContent}>
                {/* Left Column - Cart Items */}
                <div style={styles.column}>
                    {/* Cart Header Box */}
                    <div style={styles.cartHeaderBox}>
                        <span>here are 4 product in your cart</span>
                        <button style={styles.clearCartButton}>Clear Cart</button>
                    </div>

                    {/* Product Item Cards */}
                    {cartProducts.map(product => (
                        <div key={product.id} style={styles.productItemCard}>
                            <div style={styles.productImageContainer}>
                                <img src={product.image} alt={product.name} style={styles.productImage} />
                            </div>
                            <div style={styles.productDetails}>
                                <h3 style={styles.productName}>{product.name}</h3>
                                <p style={styles.productSize}>{product.size}</p>
                                <p style={styles.itemPrice}>Item Price: {product.itemPrice}</p>
                                <div style={styles.quantityControl}>
                                    <button
                                        style={styles.qtyButton}
                                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.qtyButtonHover.backgroundColor}
                                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = styles.qtyButton.backgroundColor}
                                    >
                                        −
                                    </button>
                                    <span style={styles.qtyValue}>{product.quantity}</span>
                                    <button
                                        style={styles.qtyButton}
                                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.qtyButtonHover.backgroundColor}
                                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = styles.qtyButton.backgroundColor}
                                    >
                                        +
                                    </button>
                                </div>
                                <div style={styles.productActions}>
                                    <span
                                        style={styles.actionLink}
                                        onMouseOver={(e) => e.currentTarget.style.color = styles.actionLinkHover.color}
                                        onMouseOut={(e) => e.currentTarget.style.color = styles.actionLink.color}
                                    >
                                        <FaTrashAlt /> Remove
                                    </span>
                                    <span
                                        style={styles.actionLink}
                                        onMouseOver={(e) => e.currentTarget.style.color = styles.actionLinkHover.color}
                                        onMouseOut={(e) => e.currentTarget.style.color = styles.actionLink.color}
                                    >
                                        <FaHeart /> Add Wishlist
                                    </span>
                                </div>
                            </div>
                            <span style={styles.productTotal}>Total: {product.total}</span>
                        </div>
                    ))}
                </div>

                {/* Right Column - Promo and Summary */}
                <div style={styles.column}>
                    {/* Have a Promo Code? */}
                    <div style={styles.promoCodeCard}>
                        <h2 style={styles.promoCodeHeader}>Have a Promo Code?</h2>
                        <div style={styles.promoInputGroup}>
                            <input
                                type="text"
                                placeholder="CODE.123"
                                style={styles.promoInput}
                            />
                            <button
                                style={styles.applyButton}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.applyButtonHover.backgroundColor}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = styles.applyButton.backgroundColor}
                            >
                                Apply
                            </button>
                        </div>
                    </div>

                    {/* Order Summary */}
                    <div style={styles.orderSummaryCard}>
                        <h2 style={styles.orderSummaryHeader}>Order Summary</h2>
                        <hr style={styles.summaryDivider} />

                        <div style={styles.summaryRow}>
                            <span style={styles.summaryLabel}>
                                <FaFileAlt style={styles.summaryIcon} />
                                Sub Total:
                            </span>
                            <span style={styles.summaryValue}>750.00</span>
                        </div>
                        <hr style={styles.summaryDivider} />

                        <div style={styles.summaryRow}>
                            <span style={styles.summaryLabel}>
                                <FaPercentage style={styles.summaryIcon} />
                                Discount:
                            </span>
                            <span style={styles.summaryValue}>-150.00</span>
                        </div>
                        <hr style={styles.summaryDivider} />

                        <div style={styles.summaryRow}>
                            <span style={styles.summaryLabel}>
                                <FaTruck style={styles.summaryIcon} />
                                Delivery Charges:
                            </span>
                            <span style={styles.summaryValue}>00.00</span>
                        </div>
                        <hr style={styles.summaryDivider} />

                        <div style={styles.summaryRow}>
                            <span style={styles.summaryLabel}>
                                <FaMoneyBillAlt style={styles.summaryIcon} />
                                Estimated Tax(15.5%):
                            </span>
                            <span style={styles.summaryValue}>200.00</span>
                        </div>
                        <hr style={styles.summaryDivider} />

                        <div style={{ ...styles.summaryRow, ...styles.totalAmountRow }}>
                            <span style={styles.totalAmountLabel}>Total Amount</span>
                            <span style={styles.totalAmountValue}>1000.00</span>
                        </div>

                        {/* Estimated Delivery Box */}
                        <div style={styles.estimatedDeliveryBox}>
                            <FaTruck style={styles.deliveryIcon} />
                            Estimated Delivery by 25 April, 2025
                        </div>

                        {/* Bottom Action Buttons */}
                        <div style={styles.bottomButtonGroup}>
                            <button
                                style={{ ...styles.bottomButton, ...styles.continueShoppingButton }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.continueShoppingButtonHover.backgroundColor}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = styles.continueShoppingButton.backgroundColor}
                            >
                                Continue Shopping
                            </button>
                            <button
                                style={{ ...styles.bottomButton, ...styles.buyNowButton }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.buyNowButtonHover.backgroundColor}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = styles.buyNowButton.backgroundColor}
                            >
                                Buy Now
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}